import * as React from 'react';
import cn from 'classnames';
import styles from './CreateCollectionModal.module.scss';
import messages from './messages';
import { useCreateCollectionMutation } from 'src/api/mutations/collectionMutations';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import CollectionSchema from 'src/schemas/collection';
import BaseModal from '../BaseModal';
import Button from '../Button';
import Checkbox from '../Checkbox';
import Input from '../Input';
import Paper from '../Paper';
import Textarea from '../Textarea';
import { useState } from 'react';
import { useAtom } from 'jotai';
import { Trash } from 'src/common/icons';
import { selectedAssetsAtom } from 'src/shared/selectedAssets';

type CreateCollectionModalProps = {
  onCreateCollection?: (collection: any) => void;
  /** Explicit asset ids to seed the collection with. Falls back to the global
   *  selection atom when omitted (the default assets-list behaviour). */
  assetIds?: string[];
};

export const CreateCollectionModal = ({ onCreateCollection, assetIds }: CreateCollectionModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Shared with the group by default (#958) — a collection is usually made
  // for the team; a private one is the deliberate exception.
  const [groupAccess, setGroupAccess] = useState(true);

  const { showSuccess, showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const [selectedAssets, setSelectedAssets] = useAtom(selectedAssetsAtom);

  const { closeModal } = useModal();

  const createCollectionMutation = useCreateCollectionMutation({
    onSuccess: (result) => {
      showSuccess('Collection created successfully');
      closeModal();
      onCreateCollection && onCreateCollection(result);
    },
    onError: (error) => {
      if('error' in error) {
        showAlert(error.error);
      }else{
        showAlert(getErrorMessage(error.message));
      }
      
    },
  });

  const isValid = React.useMemo(() => {
    try {
      CollectionSchema.validateSync({ name, description, group_access: groupAccess });
      return true;
    } catch (error) {
      return false;
    }
  }, [name, description, groupAccess]);

  const handleCreateCollection = () => {
    if (!isValid) return;

    createCollectionMutation.mutateAsync({
      name,
      description,
      group_access: groupAccess,
      assets: assetIds ?? selectedAssets,
    });
  };

  return (
    <BaseModal>
      <h3>Create Collection</h3>

      <section>
        <Paper>
          <Input
            variant="secondary"
            label="Collection Name"
            placeholder="Name Collection"
            value={name}
            onChange={(value) => setName(value)}
            className={styles.modalInput}
            data-test="collection-name-input"
          />

          <Textarea
            className={styles.formGroup}
            label="Description"
            placeholder="Collection's description..."
            rows={4}
            value={description}
            onChange={(value) => setDescription(value)}
            data-test="collection-description-input"
          />

          <Checkbox
            label="Group Collection"
            checked={groupAccess}
            onChange={() => setGroupAccess(!groupAccess)}
            className={styles.formGroup}
            data-test="collection-group-access-checkbox"
          />
        </Paper>

        {(assetIds ?? selectedAssets).length > 0 && (
          <Paper>
            <p>
              You have selected {(assetIds ?? selectedAssets).length} assets to add to this
              collection.
            </p>
          </Paper>
        )}
      </section>

      <div className={styles.modalActions}>
        <Button size="small" mode="tertiary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          size="small"
          mode="primary"
          onClick={handleCreateCollection}
          disabled={!isValid}
          data-test="collection-submit-button"
        >
          Create Collection
        </Button>
      </div>
    </BaseModal>
  );
};

export default CreateCollectionModal;
